import {
  APIClient,
  UnAuthorizationAPIError,
  getCookie,
  resolveAPICall,
  usePrevioCore,
  type APIRequest,
  type APISuccess,
  type APICallArgs,
  type APICallOptions,
  type APICallResult,
  type APIDataOnlyCallArgs,
  type APIFullResponseCallArgs,
} from '@previo/core'
import { usePrevioRealtime } from '@previo/realtime'
import { Environment } from '~/Environment'
import { APIPaths } from '~/http/APIPaths'
import { useDeviceIdLocalStore } from '~/store/device-id'
import { AuthRecovery } from '~/http/AuthRecovery'
import { useAuthenticationStore } from '~/store/authentication'

type PMSResponseFields = {
  warnings?: string[]
}

type PMSResponse<TResponseData> = APISuccess<TResponseData, PMSResponseFields>

type CallArgs<TOptions extends APICallOptions = APICallOptions, TBody = unknown> = APICallArgs<
  APIPaths,
  TBody,
  TOptions
>

type CallResult<TResponseData, TOptions extends APICallOptions> = APICallResult<
  TResponseData,
  PMSResponseFields,
  TOptions
>

const { useLocale } = usePrevioCore()

const lazyUsePropertyStore = async () => {
  // import lazily to prevent circular-dependencies ("can't access lexical declaration 'Http' before initialization")
  const { usePropertyStore } = await import('~/store/property/property')

  return usePropertyStore()
}

const apiClient = new APIClient<PMSResponseFields>(import.meta.env.PMS_FE_API_URL)
const authRecovery = new AuthRecovery(apiClient)

export default class Http {
  protected async callNoAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIDataOnlyCallArgs, TBody>
  ): Promise<TResponseData>

  protected async callNoAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIFullResponseCallArgs, TBody>
  ): Promise<PMSResponse<TResponseData>>

  protected async callNoAuthAPI<TBody, TResponseData, TOptions extends APICallOptions>(
    ...args: CallArgs<TOptions, TBody>
  ): Promise<CallResult<TResponseData, TOptions>> {
    return resolveAPICall<TResponseData, APIPaths, PMSResponseFields, TOptions>(args, (request) =>
      this.call<TResponseData>(request),
    )
  }

  protected async callAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIDataOnlyCallArgs, TBody>
  ): Promise<TResponseData>

  protected async callAuthAPI<TBody, TResponseData>(
    ...args: CallArgs<APIFullResponseCallArgs, TBody>
  ): Promise<PMSResponse<TResponseData>>

  protected async callAuthAPI<TBody, TResponseData, TOptions extends APICallOptions>(
    ...args: CallArgs<TOptions, TBody>
  ): Promise<CallResult<TResponseData, TOptions>> {
    return resolveAPICall<TResponseData, APIPaths, PMSResponseFields, TOptions>(args, (request) =>
      this.callWithAuth<TResponseData>(request),
    )
  }

  protected async callAuthWithPropertyAPI<TBody, TResponseData>(
    ...args: CallArgs<APIDataOnlyCallArgs, TBody>
  ): Promise<TResponseData>

  protected async callAuthWithPropertyAPI<TBody, TResponseData>(
    ...args: CallArgs<APIFullResponseCallArgs, TBody>
  ): Promise<PMSResponse<TResponseData>>

  protected async callAuthWithPropertyAPI<TBody, TResponseData, TOptions extends APICallOptions>(
    ...args: CallArgs<TOptions, TBody>
  ): Promise<CallResult<TResponseData, TOptions>> {
    const propertyStore = await lazyUsePropertyStore()

    return resolveAPICall<TResponseData, APIPaths, PMSResponseFields, TOptions>(args, (request) => {
      request.headers['X-Previo-Property-ID'] = String(propertyStore.property.propertyId!)

      return this.callWithAuth<TResponseData>(request)
    })
  }

  private async callWithAuth<TResponseData>(
    request: APIRequest,
  ): Promise<PMSResponse<TResponseData>> {
    const authorization = useAuthenticationStore.value

    if (!authorization) {
      throw new Error('Authorization token is not provided from local storage')
    }

    request.headers['Authorization'] = `Bearer ${authorization.accessToken}`

    return this.call<TResponseData>(request).catch(async (e) => {
      if (e instanceof UnAuthorizationAPIError) {
        return authRecovery.recoverAndThen(authorization.accessToken, () =>
          this.callWithAuth<TResponseData>(request),
        )
      }

      throw e
    })
  }

  private async call<TResponseData>(request: APIRequest): Promise<PMSResponse<TResponseData>> {
    if (Environment.isEnvLikeDevelopment()) {
      const xdebugSession = getCookie('XDEBUG_SESSION')

      if (xdebugSession) {
        request.query['XDEBUG_SESSION'] = xdebugSession
      }
    }

    request.headers['Accept'] ??= 'application/json'
    request.headers['Accept-Language'] ??= useLocale().get()
    request.headers['X-Previo-Tab-Uuid'] ??= usePrevioRealtime().tabUuid
    request.headers['X-Previo-Client-ID'] ??= useDeviceIdLocalStore().value

    return await apiClient.call<TResponseData>(request)
  }
}
